import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { shallow } from "zustand/shallow";
import { useOnboardingStore } from "./useOnboardingStore";
import StepProfile from "./steps/StepProfile";
import StepWatchlists from "./steps/StepWatchlists";
import StepDashboard from "./steps/StepDashboard";
import StepConcierge from "./steps/StepConcierge";
import { conciergeEnabled } from "./concierge/api";
import { CTA_PRIMARY, HEADING, LINK_MUTED, PILL } from "./ui/theme";
import type { OnboardingAnswers, OnboardingStep } from "./types";

const STEP_ORDER: OnboardingStep[] = ["profile", "watchlists", "dashboard"];

/**
 * One heading per screen: the modal owns the title so steps render content
 * only — the previous double-heading ("Welcome…" + "Here's what we'll set
 * up") was most of why the panel felt cramped.
 */
const STEP_META: Record<OnboardingStep, { title: string; sub: string }> = {
  profile: {
    title: "What do you trade?",
    sub: "Describe it in your own words, or pick your markets — we'll build a dashboard to match.",
  },
  concierge: {
    title: "Your setup",
    sub: "Built from your own words. Nothing is created until you apply.",
  },
  watchlists: {
    title: "Highlight your interests",
    sub: "Terms related to these watchlists will be highlighted in your feeds.",
  },
  dashboard: {
    title: "Your dashboard",
    sub: "A starting layout you can rearrange at any time.",
  },
};

export default function WelcomeModal() {
  const { state, setAnswers, setStep, setSelectedWatchlists, markCompleted, dismiss } =
    useOnboardingStore(
      (s) => ({
        state: s.state,
        setAnswers: s.setAnswers,
        setStep: s.setStep,
        setSelectedWatchlists: s.setSelectedWatchlists,
        markCompleted: s.markCompleted,
        dismiss: s.dismiss,
      }),
      shallow
    );

  const step: OnboardingStep = state?.lastStep ?? "profile";
  // "concierge" replaces the watchlists/dashboard steps visually (slot 1).
  const stepIndex = step === "concierge" ? 1 : STEP_ORDER.indexOf(step);
  const meta = STEP_META[step];

  // Local working copies — persisted on step transitions, not every keystroke.
  /**
   * Markets start empty.
   *
   * They used to be seeded from squawk channel roles, which put six of seven
   * cards on screen already ticked — a guess presented as the user's own
   * answer, and one that silently chose their template. An unticked grid asks
   * the question instead of assuming it.
   */
  const [answers, setLocalAnswers] = useState<OnboardingAnswers>(
    () => state?.answers ?? { assetClasses: [] }
  );
  const [selectedIds, setSelectedIds] = useState<number[]>(
    () => state?.selectedWatchlistIds ?? []
  );

  const goToStep = (next: OnboardingStep) => {
    setAnswers(answers);
    if (step === "watchlists") setSelectedWatchlists(selectedIds);
    setStep(next);
  };

  // Concierge branch point: free text present + backend enabled → the LLM
  // builds the setup instead of the manual watchlist/template steps.
  const handleContinueFromProfile = () => {
    if (conciergeEnabled && answers.freeText?.trim()) goToStep("concierge");
    else goToStep("watchlists");
  };

  const handleComplete = (appliedTemplateId: string) => {
    setAnswers(answers);
    setSelectedWatchlists(selectedIds);
    markCompleted(appliedTemplateId);
  };

  // Steps with their own CTAs own the whole footer.
  const ownsFooter = step === "dashboard" || step === "concierge";

  /**
   * Step 1 asks three ways and needs any one of them. Gating on markets alone
   * would strand someone who typed a sentence or pasted their book — which,
   * with nothing pre-ticked, is now the common case.
   */
  const profileAnswered =
    !!answers.freeText?.trim() ||
    (answers.tickers?.length ?? 0) > 0 ||
    answers.assetClasses.length > 0;

  return (
    <Dialog open onClose={() => {}} className="relative z-[100] no-drag">
      <DialogBackdrop className="fixed inset-0 bg-black/70" />
      <div className="fixed inset-0 z-10 flex items-center justify-center sm:p-6">
        <DialogPanel className="relative flex h-full w-full flex-col overflow-hidden bg-gray-900 shadow-xl sm:h-auto sm:max-h-[88vh] sm:max-w-4xl sm:rounded-lg sm:border sm:border-gray-700">

          <div className="relative flex flex-1 flex-col overflow-y-auto px-6 py-7 sm:px-9 sm:py-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-6">
              <span className={PILL}>
                Setup
                <span className="text-gray-600">/</span>
                {stepIndex + 1} of {STEP_ORDER.length}
              </span>
              <button type="button" onClick={dismiss} className={LINK_MUTED}>
                Skip for now
              </button>
            </div>

            {/* Segmented progress rail */}
            <div className="mt-5 flex items-center gap-1.5" aria-hidden>
              {STEP_ORDER.map((s, i) => (
                <span
                  key={s}
                  className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${
                    i < stepIndex
                      ? "bg-squawk-blue/40"
                      : i === stepIndex
                        ? "bg-squawk-blue"
                        : "bg-gray-700"
                  }`}
                />
              ))}
            </div>

            {/* Title — the only heading on screen. Continue sits on this row
                as well as in the footer: on a long step the footer button is
                below the fold, so the only visible way forward was "Skip for
                now" — and people were taking it. */}
            <div key={step} className="nq-onb-rise mt-7 flex items-start gap-6">
              <div className="min-w-0 flex-1">
                <DialogTitle
                  className={HEADING}
                >
                  {meta.title}
                </DialogTitle>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-300">
                  {meta.sub}
                </p>
              </div>
              {!ownsFooter && (
                <button
                  type="button"
                  onClick={() =>
                    step === "profile"
                      ? handleContinueFromProfile()
                      : goToStep(STEP_ORDER[stepIndex + 1])
                  }
                  disabled={step === "profile" && !profileAnswered}
                  className={`${CTA_PRIMARY} shrink-0`}
                >
                  Continue
                </button>
              )}
            </div>

            {/* Step body */}
            <div className="mt-7 flex-1">
              {step === "profile" && (
                <StepProfile answers={answers} onChange={setLocalAnswers} />
              )}
              {step === "concierge" && (
                <StepConcierge
                  answers={answers}
                  onComplete={handleComplete}
                  onFallback={() => goToStep("watchlists")}
                />
              )}
              {step === "watchlists" && (
                <StepWatchlists
                  answers={answers}
                  selectedIds={selectedIds}
                  onChange={setSelectedIds}
                />
              )}
              {step === "dashboard" && (
                <StepDashboard
                  answers={answers}
                  selectedWatchlistIds={selectedIds}
                  onComplete={handleComplete}
                />
              )}
            </div>

            {/* Footer */}
            <div className="mt-8 flex items-center justify-between gap-4 border-t border-gray-700 pt-5">
              <button
                type="button"
                onClick={() =>
                  goToStep(
                    step === "concierge"
                      ? "profile"
                      : STEP_ORDER[Math.max(0, stepIndex - 1)]
                  )
                }
                className={`${LINK_MUTED} ${stepIndex === 0 ? "invisible" : ""}`}
              >
                ← Back
              </button>
              {!ownsFooter && (
                <button
                  type="button"
                  onClick={() =>
                    step === "profile"
                      ? handleContinueFromProfile()
                      : goToStep(STEP_ORDER[stepIndex + 1])
                  }
                  disabled={step === "profile" && !profileAnswered}
                  className={CTA_PRIMARY}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
